/** Read-only tenant promotions (TMMPDM-0007). */
import { useEffect, useState, type ReactNode } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { EmptyState, ErrorState } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { Amount } from '@/components/ui';
import type { PromotionView } from '@/api/tmm/types';
import { formatDate, formatPercentBp } from '@/format';
import { StatusChip } from '@/status';

interface PromotionsPanelProps {
  slug: string;
}

function promotionValue(
  row: PromotionView,
  locale: string,
  t: (key: string, fallback?: string, values?: Record<string, string | number>) => string,
): ReactNode {
  if (row.kind === 'AMOUNT_OFF_VARIABLE' && row.amountMinor != null) {
    // Money is always shown with its currency; one the API didn't send is not guessed.
    return row.currency ? (
      <Amount minor={row.amountMinor} currency={row.currency} locale={locale} />
    ) : (
      '—'
    );
  }
  if (row.kind === 'PERCENT_OFF_VARIABLE' && row.percentBp != null) {
    return formatPercentBp(row.percentBp, locale, 1);
  }
  if (row.kind === 'TRIAL_DAYS' && row.trialDays != null) {
    return t('common.days', '{{count}} days', { count: row.trialDays });
  }
  if (row.kind === 'OFFER' && row.retailOfferId != null) {
    return String(row.retailOfferId);
  }
  return '—';
}

export function PromotionsPanel({ slug }: PromotionsPanelProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const [reloadToken, setReloadToken] = useState(0);
  const [rows, setRows] = useState<PromotionView[] | null>(null);
  const [error, setError] = useState<{ cause: unknown } | null>(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listPromotions(slug)
      .then((list) => {
        if (!cancelled) {
          setRows(list.promotions);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setRows(null);
          setError({ cause: err });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, reloadToken]);

  if (error) {
    return (
      <ErrorState
        error={error.cause}
        message={t('tenantDetail.promotions.loadError', 'Could not load promotions.')}
        onRetry={() => setReloadToken((n) => n + 1)}
      />
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={t('tenantDetail.promotions.tableAria', 'Tenant promotions')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('tenantDetail.promotions.col.code', 'Code')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.name', 'Name')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.kind', 'Kind')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.value', 'Value')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.valid', 'Valid')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.redemptions', 'Redemptions')}</TableCell>
            <TableCell>{t('tenantDetail.promotions.col.status', 'Status')}</TableCell>
            <TableCell>
              {t('tenantDetail.promotions.col.newOnly', 'New subscribers only')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows === null ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                {t('common.loading', 'Loading')}
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center">
                <EmptyState title={t('tenantDetail.promotions.empty', 'No promotions.')} />
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.code}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{t(`tenantDetail.promotions.kind.${row.kind}`, row.kind)}</TableCell>
                <TableCell>{promotionValue(row, locale, t)}</TableCell>
                <TableCell>
                  {formatDate(row.validFrom, locale)}
                  {' – '}
                  {formatDate(row.validUntil, locale)}
                </TableCell>
                <TableCell>
                  {row.redemptionCount} / {row.maxRedemptions ?? t('common.unlimited', '∞')}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={row.status}
                    label={t(`tenantDetail.promotions.status.${row.status}`, row.status)}
                  />
                </TableCell>
                <TableCell>
                  {row.newSubscribersOnly ? <CheckIcon fontSize="small" /> : null}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
